import { Badge, Box, Button, Collapse, Flex, Text, Textarea, useDisclosure } from '@chakra-ui/react'
import { getDateString, LabResult, LabResultFlag, SaveLabResultArgs, WithId } from '@hb/shared'
import { useCallback, useState } from 'react'

const flagColorScheme: Record<LabResultFlag, string> = {
  normal: 'gray',
  high: 'red',
  low: 'orange',
  abnormal: 'purple',
  critical: 'red',
}

const flagLabel: Record<LabResultFlag, string> = {
  normal: 'Normal',
  high: 'HIGH',
  low: 'LOW',
  abnormal: 'ABN',
  critical: 'CRITICAL',
}

const noteRequiredFlags: LabResultFlag[] = ['abnormal', 'critical']

interface Props {
  result: WithId<LabResult>
  onSave: (args: Partial<SaveLabResultArgs> & { labResultId: string }) => Promise<void>
}

export const LabResultRow = ({ result, onSave }: Props) => {
  const isAcknowledged = !!result.acknowledgedAt
  const isFlagged = result.flagStatus !== 'normal'
  const noteRequired = noteRequiredFlags.includes(result.flagStatus)
  const { isOpen: isNoteOpen, onToggle: toggleNote } = useDisclosure()

  const [note, setNote] = useState(result.providerNote ?? '')
  const [saving, setSaving] = useState(false)

  const handleAcknowledge = useCallback(async () => {
    setSaving(true)
    try {
      await onSave({
        labResultId: result.id,
        acknowledgedBy: result.acknowledgedBy ?? '__pending__',
        providerNote: note || undefined,
      })
    } finally {
      setSaving(false)
    }
  }, [onSave, result.id, result.acknowledgedBy, note])

  const handleSaveNote = useCallback(async () => {
    setSaving(true)
    try {
      await onSave({ labResultId: result.id, providerNote: note })
    } finally {
      setSaving(false)
    }
  }, [onSave, result.id, note])

  const dateStr = result.resultDate
    ? getDateString(result.resultDate, 'short', true)
    : result.collectedDate
      ? getDateString(result.collectedDate, 'short', true)
      : '—'

  return (
    <Box
      bg="white"
      _dark={{
        bg: 'gray.800',
        borderColor: isFlagged && !isAcknowledged ? 'orange.700' : 'whiteAlpha.200',
      }}
      borderRadius={6}
      border="1px solid"
      borderColor={isFlagged && !isAcknowledged ? 'orange.200' : 'gray.200'}
      p={3}>
      <Flex align="center" gap={2} flexWrap="wrap">
        <Text fontFamily="Hero-New" fontWeight={700} fontSize="sm">
          {result.labName}
        </Text>
        <Badge colorScheme={flagColorScheme[result.flagStatus]} fontFamily="Hero-New" fontSize="xs">
          {flagLabel[result.flagStatus]}
        </Badge>
        <Text fontFamily="Hero-New" fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
          {result.result}
          {result.unit ? ` ${result.unit}` : ''}
        </Text>
        {result.referenceRange && (
          <Text fontFamily="Hero-New" fontSize="xs" color="gray.400">
            Ref: {result.referenceRange}
          </Text>
        )}
        <Text fontFamily="Hero-New" fontSize="xs" color="gray.400" ml="auto">
          {dateStr}
        </Text>
      </Flex>

      {/* Acknowledged state */}
      {isAcknowledged && (
        <Flex align="center" gap={2} mt={2} flexWrap="wrap">
          <Badge colorScheme="green" fontFamily="Hero-New" fontSize="xs">
            Acknowledged
          </Badge>
          {result.providerNote && (
            <Button
              size="xs"
              variant="link"
              colorScheme="gray"
              fontFamily="Hero-New"
              onClick={toggleNote}>
              {isNoteOpen ? 'Hide note' : 'View note'}
            </Button>
          )}
        </Flex>
      )}
      {isAcknowledged && result.providerNote && (
        <Collapse in={isNoteOpen} animateOpacity>
          <Text
            mt={2}
            fontSize="xs"
            color="gray.600"
            _dark={{ color: 'gray.400' }}
            fontFamily="Hero-New"
            whiteSpace="pre-wrap">
            {result.providerNote}
          </Text>
        </Collapse>
      )}

      {/* Unacknowledged + flagged: note + acknowledge */}
      {!isAcknowledged && isFlagged && (
        <Box mt={2}>
          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={
              noteRequired
                ? 'Clinical note required before acknowledging…'
                : 'Clinical note (optional)…'
            }
            fontFamily="Hero-New"
            fontSize="xs"
            rows={2}
            size="sm"
          />
          <Flex mt={1.5} gap={2} justify="flex-end">
            <Button
              size="xs"
              colorScheme="green"
              fontFamily="Hero-New"
              onClick={handleAcknowledge}
              isLoading={saving}
              isDisabled={noteRequired && !note.trim()}>
              Acknowledge
            </Button>
          </Flex>
        </Box>
      )}

      {/* Acknowledged: allow editing note */}
      {isAcknowledged && (
        <Collapse in={isNoteOpen} animateOpacity>
          <Box mt={2}>
            <Textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Edit note…"
              fontFamily="Hero-New"
              fontSize="xs"
              rows={2}
              size="sm"
            />
            <Flex mt={1.5} justify="flex-end">
              <Button
                size="xs"
                colorScheme="blue"
                fontFamily="Hero-New"
                onClick={handleSaveNote}
                isLoading={saving}
                isDisabled={note === (result.providerNote ?? '')}>
                Save Note
              </Button>
            </Flex>
          </Box>
        </Collapse>
      )}
    </Box>
  )
}
